import { themeQuartz, type ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import type { RootState } from '../../../../state/store';

interface ITableViewProps {
  data: any[];
}

const TableView = ({ data }: ITableViewProps) => {
  const theme = useSelector((state: RootState) => state.theme.mode);
  const currencyCode = useSelector(
    (state: RootState) => (state.company.company_details as any)?.currency || ''
  );
  const gridRef = useRef<any>(null);

  // Helper function to convert snake_case to Title Case
  const formatHeaderName = useCallback((fieldName: string): string => {
    return fieldName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, []);

  // Number formatter for currency and large numbers
  const numberFormatter = useCallback(
    (params: any) => {
      if (typeof params.value === 'number') {
        // For total_amount and total_discount, format without currency symbol
        if (params.colDef.field === 'total_amount' || params.colDef.field === 'total_discount') {
          return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(params.value);
        }
        // Format other currency fields with currency symbol
        if (
          params.colDef.field?.toLowerCase().includes('amount') ||
          params.colDef.field?.toLowerCase().includes('total') ||
          params.colDef.field?.toLowerCase().includes('discount')
        ) {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 2
          }).format(params.value);
        }
        // Format large numbers with commas
        if (params.value > 1000) {
          return params.value.toLocaleString();
        }
      }
      return params.value;
    },
    [currencyCode]
  );

  // Date formatter for timestamp fields
  const dateFormatter = useCallback((params: any) => {
    if (params.colDef.field === 'order_date' && typeof params.value === 'number') {
      // Convert timestamp to readable date
      const date = new Date(params.value * 1000);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return params.value;
  }, []);

  // Column definitions
  const columnDefs: ColDef[] = useMemo(() => {
    if (!data || data.length === 0) return [];

    const sampleRow = data[0];
    if (!sampleRow || typeof sampleRow !== 'object') return [];

    // Fields to exclude from display
    const excludeFields = ['rownum', 'total_records', 'offset', 'order_id', 'staff_id', 'timezone'];

    // Add serial number column first
    const serialNumberColumn: ColDef = {
      headerName: '#',
      headerClass: 'centered-header',
      valueGetter: (params: any) => {
        const rowIndex = params.node?.rowIndex || 0;
        return rowIndex + 1;
      },
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false,
      suppressHeaderMenuButton: true,
      flex: 0,
      cellStyle: {
        textAlign: 'center',
        backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa',
        borderRight: theme === 'dark' ? '1px solid #404040' : '1px solid #dee2e6',
        fontWeight: '500',
        color: theme === 'dark' ? '#e5e5e5' : '#495057'
      }
    };

    const dataColumns = Object.keys(sampleRow)
      .filter((key) => !excludeFields.includes(key))
      .map((fieldName) => {
        const sampleValue = sampleRow[fieldName];
        const isNumeric = typeof sampleValue === 'number';
        const isCurrency =
          fieldName.toLowerCase().includes('amount') ||
          fieldName.toLowerCase().includes('total') ||
          fieldName.toLowerCase().includes('discount');
        const isDate = fieldName === 'order_date';
        const isId = fieldName.toLowerCase().includes('_id') || fieldName === 'order_no';

        // Add currency to header for specific columns
        let headerName = formatHeaderName(fieldName);
        if (fieldName === 'total_amount' || fieldName === 'total_discount') {
          headerName = `${headerName} (${currencyCode})`;
        }

        // Define columns that should not have filters
        const columnsWithoutFilters = [
          'order_date',
          'customer_phone',
          'customer_email',
          'payment_type',
          'no_of_covers',
          'total_amount',
          'total_discount'
        ];

        const colDef: ColDef = {
          field: fieldName,
          headerName: headerName,
          headerClass: 'centered-header',
          sortable: true,
          filter: !columnsWithoutFilters.includes(fieldName),
          resizable: true,
          flex: 1,
          minWidth: 150,
          suppressSizeToFit: false
        };

        // Apply specific formatting based on field type
        if (isDate) {
          colDef.valueFormatter = dateFormatter;
          colDef.minWidth = 180;
        } else if (isCurrency || isNumeric) {
          colDef.valueFormatter = numberFormatter;
          colDef.cellClass = 'ag-cell-right-align';
          colDef.headerClass = 'centered-header ag-header-cell-right';
        } else if (isId) {
          colDef.cellClass = 'ag-cell-center-align';
          colDef.minWidth = 100;
        } else {
          colDef.cellClass = 'ag-cell-left-align';
        }

        // Special styling for specific fields
        if (fieldName === 'order_status') {
          colDef.cellStyle = (params) => {
            const status = params.value?.toLowerCase();
            if (status === 'paid') {
              return {
                color: theme === 'dark' ? '#4ade80' : '#16a34a',
                fontWeight: '600'
              };
            } else if (status === 'pending') {
              return {
                color: theme === 'dark' ? '#fbbf24' : '#d97706',
                fontWeight: '600'
              };
            }
            return null;
          };
        }

        return colDef;
      });

    // Combine serial number column with data columns
    return [serialNumberColumn, ...dataColumns];
  }, [data, theme, formatHeaderName, numberFormatter, dateFormatter, currencyCode]);

  const defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    suppressHeaderMenuButton: true,
    headerClass: 'centered-header',
    autoHeight: false,
    wrapText: false,
    cellClass: (params: any) => {
      const classes = [];

      // Add alignment class based on content type
      const isNumeric =
        typeof params.value === 'number' ||
        (typeof params.value === 'string' && !isNaN(parseFloat(params.value)));

      const isCurrency =
        params.colDef.field?.toLowerCase().includes('total') ||
        params.colDef.field?.toLowerCase().includes('amount') ||
        params.colDef.field?.toLowerCase().includes('cost') ||
        params.colDef.field?.toLowerCase().includes('price');

      classes.push(isNumeric || isCurrency ? 'ag-cell-right-align' : 'ag-cell-left-align');

      return classes.join(' ');
    }
  };

  const reportTheme = useMemo(() => {
    return theme === 'dark'
      ? themeQuartz.withParams({
          accentColor: '#0D8A6A',
          backgroundColor: '#1a1a1a',
          borderColor: '#404040',
          borderRadius: 4,
          browserColorScheme: 'dark',
          cellTextColor: '#e5e5e5',
          columnBorder: true,
          fontSize: 14,
          foregroundColor: '#e5e5e5',
          headerBackgroundColor: '#2d2d2d',
          headerFontSize: 14,
          headerTextColor: '#ffffff',
          oddRowBackgroundColor: '#262626',
          wrapperBorderRadius: 8
        })
      : themeQuartz.withParams({
          accentColor: '#0D8A6A',
          backgroundColor: '#f4f6f6',
          borderRadius: 4,
          browserColorScheme: 'inherit',
          fontSize: 14,
          headerFontSize: 14,
          headerTextColor: '#374151',
          wrapperBorderRadius: 8,
          borderColor: '#d1d5db',
          columnBorder: true,
          headerBackgroundColor: '#d8edea',
          oddRowBackgroundColor: '#ffffff',
          cellTextColor: '#374151',
          foregroundColor: '#374151'
        });
  }, [theme]);

  if (!data || data.length === 0) {
    return (
      <StyledTableSection>
        <div className="no-data-container">
          <div className="no-data-message">
            <p>No Data Available</p>
          </div>
        </div>
      </StyledTableSection>
    );
  }

  return (
    <StyledTableSection>
      <div className="table-container">
        <GridContainer>
          <AgGridReact
            ref={gridRef}
            theme={reportTheme}
            rowData={data}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={20}
            paginationPageSizeSelector={[10, 20, 50, 100]}
            animateRows={true}
            headerHeight={32}
            rowHeight={40}
            onGridReady={(params) => {
              gridRef.current = params.api;
              setTimeout(() => {
                params.api.sizeColumnsToFit();
              }, 100);
            }}
            onFirstDataRendered={(params) => {
              setTimeout(() => {
                params.api.sizeColumnsToFit();
              }, 100);
            }}
          />
        </GridContainer>
      </div>
    </StyledTableSection>
  );
};

const StyledTableSection = styled.div`
  position: relative;
  height: 600px;
  width: 100%;
  max-width: 100vw;
  overflow-x: auto;

  .table-container {
    position: relative;
    height: 100%;
    width: 100%;
  }

  .no-data-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    width: 100%;
  }

  .no-data-message {
    text-align: center;
    color: #6b7280;
    font-size: 16px;
  }

  .ag-theme-quartz {
    .ag-root-wrapper {
      border: none;
      border-radius: 0;
    }

    .ag-root {
      border: none;
    }

    .ag-body {
      min-height: 0;
    }
  }
`;

const GridContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  overflow: visible;
  display: block;
  margin: 0;
  padding: 0;
  box-sizing: border-box;

  .ag-root {
    margin-bottom: 0;
  }

  .ag-root-wrapper {
    border-bottom: none;
    min-height: 0;
  }

  .ag-body-viewport {
    min-height: 0;
  }

  /* Center align all column headers - AG-Grid specific selectors */
  .ag-header-cell-label {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    width: 100% !important;
  }

  .ag-header-cell-text {
    text-align: center !important;
    width: 100% !important;
    display: block !important;
  }

  .ag-header-cell-comp-wrapper {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    width: 100% !important;
  }

  /* Target the header cells directly */
  .ag-header-cell {
    .ag-header-cell-label {
      justify-content: center !important;
    }
    overflow: visible !important;
  }

  /* Override any custom header component styles */
  .ag-header-cell .ag-react-container {
    width: 100% !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .ag-cell-right-align {
    justify-content: flex-end;
    text-align: right;
    white-space: nowrap;
    padding: 8px;
  }

  .ag-cell-left-align {
    justify-content: flex-start;
    text-align: left;
    white-space: nowrap;
    padding: 8px;
  }

  .ag-cell-center-align {
    justify-content: center;
    text-align: center;
    white-space: nowrap;
    padding: 8px;
  }
`;

export default TableView;